import { Injectable } from "@angular/core";
import { ApplicationState } from "@feature/store/application-store";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { EMPTY } from "rxjs";
import { catchError, map, tap, mergeMap } from "rxjs/operators";
import { TableHttpService } from "../services/table-http.service";
import * as tableActions from "./table.actions";

@Injectable()
export class TableEffects {
  constructor(
    private readonly store: Store<ApplicationState>,
    private readonly actions: Actions,
    private readonly tableHttpService: TableHttpService
  ) {}

  loadTable = createEffect(() =>
    this.actions.pipe(
      ofType(tableActions.loadTable),
      mergeMap((action) =>
        this.tableHttpService.getTable(action.entityName, action.key, action.widgetId).pipe(
          map((table) =>
            tableActions.loadTableComplete({ id: action.id, table })
          ),
          catchError((error) => {
            console.log(error);
            tableActions.loadTableError({ id: action.id, error });
            return EMPTY;
          })
        )
      )
    )
  );

  getAvailableColumns = createEffect(() =>
    this.actions.pipe(
      ofType(tableActions.getAvailableColumns),
      mergeMap((action) =>
        this.tableHttpService.getAvailableColumns(action.entityName, action.widgetId).pipe(
          map((columns) =>
            tableActions.getAvailableColumnsComplete({ id: action.id, columns })
          ),
          catchError((error) => {
            console.log(error);
            tableActions.getAvailableColumnsError({ id: action.id, error });
            return EMPTY;
          })
        )
      )
    )
  );

  getAllColumns = createEffect(() => 
    this.actions.pipe(
      ofType(tableActions.getAllColumns),
      mergeMap((action) => 
        this.tableHttpService.getAllColumns(action.entityName).pipe(
          map((allColumns) => 
            tableActions.getAllColumnsComplete({id: action.id, allColumns})
            ),
            catchError((error) => {
              console.log(error);
              tableActions.getAllColumnsError({ id: action.id, error });
            return EMPTY;
            })
        ))
    ))

  setSelectedColumns = createEffect(() =>
    this.actions.pipe(
      ofType(tableActions.setSelectedColumns),
      mergeMap((action) =>
        this.tableHttpService
          .setSelectedColumns(action.entityName, action.selectedColumns, action.widgetId)
          .pipe(
            map(() =>
              tableActions.setSelectedColumnsComplete({ id: action.id, entityName: action.entityName, widgetId: action.widgetId })
            ),
            tap(() =>
              this.store.dispatch(
                tableActions.loadTable({
                  id: action.id,
                  entityName: action.entityName,
                  key: action.key,
                  widgetId: action.widgetId
                })
              )
            ),
            catchError((error) => {
              console.log(error);
              tableActions.setSelectedColumnsError({ id: action.id, error });
              return EMPTY;
            })
          )
      )
    )
  );

  setSelectedColumnsComplete = createEffect(() =>
    this.actions.pipe(
      ofType(tableActions.setSelectedColumnsComplete),
      mergeMap(payload => [
        tableActions.getAvailableColumns({id: payload.id, entityName: payload.entityName, widgetId: payload.widgetId})
      ]))
  );

  reorderColumns = createEffect(() =>
    this.actions.pipe(
      ofType(tableActions.reorderColumns),
      mergeMap((action) =>
        this.tableHttpService
          .reorderColumns(action.entityName, action.orderedColumns, action.widgetId)
          .pipe(
            map(() => tableActions.reorderColumnsComplete({ id: action.id })),
            catchError((error) => {
              console.log(error);
              tableActions.reorderColumnsError({ id: action.id, error });
              return EMPTY;
            })
          )
      )
    )
  );

  resizeColumn = createEffect(() =>
    this.actions.pipe(
      ofType(tableActions.resizeColumn),
      mergeMap((action) =>
        this.tableHttpService
          .resizeColumn(action.entityName, action.resizedColumn, action.widgetId)
          .pipe(
            map(() => tableActions.resizeColumnComplete({ id: action.id })),
            catchError((error) => {
              console.log(error);
              tableActions.resizeColumnError({ id: action.id, error });
              return EMPTY;
            })
          )
      )
    )
  );

  resetColumnsToDefault = createEffect(() =>
    this.actions.pipe(
      ofType(tableActions.resetColumnsToDefault),
      mergeMap((action) =>
        this.tableHttpService.resetColumnsToDefault(action.entityName, action.widgetId).pipe(
          map(() =>
            tableActions.resetColumnsToDefaultComplete({ id: action.id })
          ),
          tap(() =>
            this.store.dispatch(
              tableActions.loadTable({
                id: action.id,
                entityName: action.entityName,
                key: action.key,
                widgetId: action.widgetId
              })
            )
          ),
          catchError((error) => {
            console.log(error);
            tableActions.resetColumnsToDefaultError({ id: action.id, error });
            return EMPTY;
          })
        )
      )
    )
  );
}
